import {
  buildSyneriseCampaignQueryUrl,
  IS_SYNERISE_PERSONALIZED_RECOMMENDATION_ENABLED,
  IS_SYNERISE_RECOMMENDATION_ENABLED,
  IS_SYNERISE_SIMILAR_ITEMS_RECOMMENDATION_ENABLED,
  RecommendationMetadataType,
  SYNERISE_PERSONALIZED_RECOMMENDATION_CAMPAIGN_ID,
  SYNERISE_SIMILAR_ITEMS_RECOMMENDATION_CAMPAIGN_ID,
  SyneriseCampaignRecommendationQueryParams,
  SyneriseCampainRecommendationApiResponse,
  syneriseClientApi,
  waitForSyneriseClientUUID} from 'lib/synerise'
import { isNilOrEmpty, isNotNilOrEmpty } from 'shared/utils/ramda'

import {
  ProductTypes,
  RecommendedProductsResponseTypes
} from '../types/products'

import {
  getRecommendedProductsByCategory,
  getRecommendedProductsByIds} from './products'

/* 
Synerise Recommendation API reference: https://developers.synerise.com/AIRecommendations/AIRecommendations.html#tag/Recommendations
Synerise Recommendation developer guidelines: https://hub.synerise.com/developers/api/recommendations/
*/

export interface BaseRecommendationParams {
  token: string
  distributionCenterId: string
  syneriseRecommendationQueryParams: SyneriseCampaignRecommendationQueryParams
}

interface PersonalizedRecommendationParams extends BaseRecommendationParams {}

interface SimilarItemRecommendationParams extends BaseRecommendationParams {
  productId: string
  categoryId: string
}

const getSyneriseRecommendedProductData = async (query: string) => {
  const result: SyneriseCampainRecommendationApiResponse =
    await syneriseClientApi.get(query)

  return {
    productIds: Array.isArray(result?.data?.data)
      ? result.data.data.map(item => item.attributes.product_id)
      : [],
    correlationId: result?.data?.extras?.correlationId || null
  }
}

export const getSyneriseRecommendedProductsByCampaign = async (
  campaignId: string,
  syneriseRecommendationQueryParams: SyneriseCampaignRecommendationQueryParams,
  distributionCenterId: string,
  token: string
): Promise<{
  syneriseRecommendedProducts: ProductTypes[]
  correlationId: string | null
}> => {
  // 1. Check feature flag
  if (!IS_SYNERISE_RECOMMENDATION_ENABLED) {
    return {
      syneriseRecommendedProducts: [],
      correlationId: null
    }
  }

  try {
    // 2. Build query
    const synersieQuery = buildSyneriseCampaignQueryUrl(
      campaignId,
      syneriseRecommendationQueryParams
    )

    // 3. Get Synerise recommended IDs
    const { productIds: syneriseIds, correlationId } =
      await getSyneriseRecommendedProductData(synersieQuery)

    // 4. Return early if no recommendation IDs
    if (isNilOrEmpty(syneriseIds)) {
      return {
        syneriseRecommendedProducts: [],
        correlationId
      }
    }

    // 5. Get recommended products
    const syneriseRecommendedProducts = await getRecommendedProductsByIds(
      syneriseIds,
      distributionCenterId,
      token
    )

    return {
      syneriseRecommendedProducts: syneriseRecommendedProducts || [],
      correlationId
    }
  } catch (err) {
    console.error('Synerise recommendations failed', err)

    return {
      syneriseRecommendedProducts: [],
      correlationId: null
    }
  }
}

const getSimilarItemRecommendedProductsFallback = async (
  categoryId: string,
  productId: string,
  token: string
) => {
  if (!categoryId || !productId) return []
  try {
    const fallback: RecommendedProductsResponseTypes =
      await getRecommendedProductsByCategory({ categoryId, productId, token })

    return fallback?.data?.data || []
  } catch (err) {
    console.error('Similar item recommendations fallback failed', err)

    return []
  }
}

// Main exported functions used in useQuery hooks

export type getRecommendedProductsReturnType = {
  recommendationProducts: ProductTypes[]
  recommendationMetadata: RecommendationMetadataType | null
}

export const getRecommendedSimilarItemProducts = async ({
  token,
  categoryId,
  productId,
  distributionCenterId,
  syneriseRecommendationQueryParams
}: SimilarItemRecommendationParams): Promise<getRecommendedProductsReturnType> => {
  let syneriseRecommendedProducts: ProductTypes[] = []
  let correlationId: string | null = null

  if (IS_SYNERISE_SIMILAR_ITEMS_RECOMMENDATION_ENABLED) {
    // 1. Get Synerise client UUID if available
    syneriseRecommendationQueryParams.clientUUID =
      await waitForSyneriseClientUUID()

    // 2. Get Synerise recommendations if product ID is present
    if (isNotNilOrEmpty(syneriseRecommendationQueryParams.itemId))
      ({ syneriseRecommendedProducts, correlationId } =
        await getSyneriseRecommendedProductsByCampaign(
          SYNERISE_SIMILAR_ITEMS_RECOMMENDATION_CAMPAIGN_ID,
          syneriseRecommendationQueryParams,
          distributionCenterId,
          token
        ))

    // 3. Check Synerise recommendations and return
    if (isNotNilOrEmpty(syneriseRecommendedProducts))
      return {
        recommendationProducts: syneriseRecommendedProducts,
        recommendationMetadata: {
          correlationId,
          campaignId: SYNERISE_SIMILAR_ITEMS_RECOMMENDATION_CAMPAIGN_ID
        }
      }
  }

  // 4. Get fallback from main API
  const fallbackRecommendedProducts =
    await getSimilarItemRecommendedProductsFallback(
      categoryId,
      productId,
      token
    )

  return {
    recommendationProducts: fallbackRecommendedProducts || [],
    recommendationMetadata: null
  }
}

export const getRecommendedPersonalizedProducts = async ({
  token,
  distributionCenterId,
  syneriseRecommendationQueryParams
}: PersonalizedRecommendationParams): Promise<getRecommendedProductsReturnType> => {
  let syneriseRecommendedProducts: ProductTypes[] = []
  let correlationId: string | null = null

  if (IS_SYNERISE_PERSONALIZED_RECOMMENDATION_ENABLED) {
    // 1. Get Synerise recommendations if synerise user UUID is passed
    if (isNotNilOrEmpty(syneriseRecommendationQueryParams.clientUUID))
      ({ syneriseRecommendedProducts, correlationId } =
        await getSyneriseRecommendedProductsByCampaign(
          SYNERISE_PERSONALIZED_RECOMMENDATION_CAMPAIGN_ID,
          syneriseRecommendationQueryParams,
          distributionCenterId,
          token
        ))
  }

  // 2. Return Synerise recommendations - no fallback for this recommendation
  return {
    recommendationProducts: syneriseRecommendedProducts || [],
    recommendationMetadata: {
      correlationId,
      campaignId: SYNERISE_PERSONALIZED_RECOMMENDATION_CAMPAIGN_ID
    }
  }
}
